import {
  SubmitCaptchaRequest,
  RecaptchaData,
  HCaptchaData,
  TurnstileData,
  FunCaptchaData,
  GeeTestData,
  GeeTestV4Data,
  TextCaptchaData,
  ImageCaptchaData
} from './types';

/**
 * Helper functions to create captcha requests with proper typing
 */

export const createTextCaptcha = (data: TextCaptchaData): SubmitCaptchaRequest => ({
  type: 'text',
  ...data
});

export const createImageCaptcha = (data: ImageCaptchaData): SubmitCaptchaRequest => ({
  type: 'image',
  ...data
});

export const createRecaptchaV2 = (data: RecaptchaData): SubmitCaptchaRequest => ({
  type: 'recaptcha_v2',
  ...data
});

export const createRecaptchaV2Invisible = (data: RecaptchaData): SubmitCaptchaRequest => ({
  type: 'recaptcha_v2_invisible',
  ...data
});

export const createRecaptchaV3 = (data: RecaptchaData): SubmitCaptchaRequest => ({
  type: 'recaptcha_v3',
  ...data
});

export const createHCaptcha = (data: HCaptchaData): SubmitCaptchaRequest => ({
  type: 'hcaptcha',
  ...data
});

export const createTurnstile = (data: TurnstileData): SubmitCaptchaRequest => ({
  type: 'turnstile',
  ...data
});

export const createFunCaptcha = (data: FunCaptchaData): SubmitCaptchaRequest => ({
  type: 'funcaptcha',
  ...data
});

export const createGeeTest = (data: GeeTestData): SubmitCaptchaRequest => ({
  type: 'geetest',
  ...data
});

export const createGeeTestV4 = (data: GeeTestV4Data): SubmitCaptchaRequest => ({
  type: 'geetest_v4',
  ...data
});

/**
 * Validation helpers
 */

export const validateApiKey = (apiKey: string): boolean => {
  return typeof apiKey === 'string' && apiKey.length > 0;
};

export const validateUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

export const validateSiteKey = (siteKey: string): boolean => {
  return typeof siteKey === 'string' && siteKey.length > 0;
};

/**
 * Utility functions
 */

export const isBase64 = (str: string): boolean => {
  try {
    return btoa(atob(str)) === str;
  } catch {
    return false;
  }
};

export const encodeImageToBase64 = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data:image/...;base64, prefix
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export const sleep = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Cost calculation helpers
 */

export const getCaptchaCost = (type: string): number => {
  const costs: Record<string, number> = {
    text: 0.1,
    image: 0.2,
    recaptcha_v2: 1.0,
    recaptcha_v2_invisible: 1.0,
    recaptcha_v3: 1.5,
    hcaptcha: 1.0,
    funcaptcha: 2.0,
    geetest: 2.0,
    geetest_v4: 2.2,
    turnstile: 1.0
  };
  
  return costs[type] || 1.0;
};

/**
 * Browser detection helpers
 */

export const getBrowserInfo = () => {
  if (typeof window === 'undefined') {
    return {
      userAgent: 'Node.js',
      language: 'en-US',
      platform: 'server'
    };
  }

  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform
  };
};

/**
 * Error message helpers
 */

export const getErrorMessage = (error: any): string => {
  if (error?.response?.data?.error) {
    return error.response.data.error;
  }
  
  if (error?.message) {
    return error.message;
  }
  
  return 'Unknown error occurred';
};

export const getErrorCode = (error: any): string | undefined => {
  return error?.response?.data?.errorCode || error?.code;
};

/**
 * Retry helpers
 */

export const withRetry = async <T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> => {
  let lastError: Error;
  
  for (let i = 0; i <= maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;
      
      if (i === maxRetries) {
        throw lastError;
      }
      
      await sleep(delay * Math.pow(2, i)); // Exponential backoff
    }
  }
  
  throw lastError!;
};

/**
 * Proxy helpers
 */

export const formatProxy = (proxy: {
  host: string;
  port: number;
  username?: string;
  password?: string;
  type?: 'http' | 'https' | 'socks4' | 'socks5';
}): string => {
  const { host, port, username, password, type = 'http' } = proxy;
  
  if (username && password) {
    return `${type}://${username}:${password}@${host}:${port}`;
  }
  
  return `${type}://${host}:${port}`;
};

/**
 * Task status helpers
 */

export const isTaskCompleted = (status: string): boolean => {
  return status === 'completed';
};

export const isTaskFailed = (status: string): boolean => {
  return status === 'failed';
};

export const isTaskPending = (status: string): boolean => {
  return status === 'pending' || status === 'processing';
};
